// ✅ src/components/screens/SellerPayScreen.js (FULL)
// ✅ Initializes Paystack + redirects
// ✅ callbackUrl = /seller/pay/callback

import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";

function safe(v) {
  return String(v ?? "").trim();
}

function moneyGHS(n) {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return "GHS 0";
  return `GHS ${num.toLocaleString()}`;
}

// ✅ IMPORTANT: Use your deployed Cloud Run URL (initialize)
const API = {
  initializePayment: "https://initializepayment-rtodv3mixa-uc.a.run.app",
  // ⚠️ You MUST have a verify endpoint too (see callback screen below)
  // verifyPayment: "https://verifypayment-xxxxx-uc.a.run.app",
};

export default function SellerPayScreen({ goBack }) {
  /** @type {[import("firebase/auth").User|null, Function]} */
  const [user, setUser] = useState(null);
  /** @type {[boolean, Function]} */
  const [loading, setLoading] = useState(true);
  /** @type {[string, Function]} */
  const [msg, setMsg] = useState("");
  /** @type {["info"|"error"|"success", Function]} */
  const [msgType, setMsgType] = useState("info");
  /** @type {[boolean, Function]} */
  const [paying, setPaying] = useState(false);

  /** @type {number} */
  const AMOUNT = 60; // GHS
  /** @type {string} */
  const CURRENCY = "GHS";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setLoading(false);

      if (!u) {
        setMsgType("error");
        setMsg("Please login first to activate your store.");
      } else {
        setMsgType("info");
        setMsg("");
      }
    });
    return () => unsub();
  }, []);

  const titleText = useMemo(() => "Subscription Payment", []);
  const subtitleText = useMemo(
    () => "Pay to activate your store for 30 days. You can renew anytime.",
    []
  );

  const startPayment = async () => {
    if (!user || paying) return;

    setPaying(true);
    setMsg("");
    setMsgType("info");

    try {
      const callbackUrl = `${window.location.origin}/seller/pay/callback`;

      const res = await fetch(API.initializePayment, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: user.email,
          amount: AMOUNT,
          currency: CURRENCY,
          uid: user.uid,
          callbackUrl,
          meta: {
            plan: "MONTHLY",
            purpose: "STORE_SUBSCRIPTION",
          },
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Failed to initialize payment.");
      }

      const url =
        data?.authorization_url ||
        data?.data?.authorization_url ||
        data?.data?.authorizationUrl;

      if (!url) throw new Error("No Paystack authorization URL returned.");

      window.location.href = url;
    } catch (e) {
      setMsgType("error");
      setMsg(e?.message || "Payment failed.");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.wrap}>
          <div style={S.skeletonCard}>
            <div style={S.skelLineLg} />
            <div style={S.skelLine} />
            <div style={S.skelBtn} />
          </div>
        </div>
      </div>
    );
  }

  const amountLabel = moneyGHS(AMOUNT);

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.topbar}>
          <button
            onClick={goBack ? goBack : () => window.history.back()}
            style={S.backBtn}
          >
            <span style={{ fontSize: 18, lineHeight: "18px" }}>←</span>
            <span>Back</span>
          </button>

          <div style={S.topTitleWrap}>
            <div style={S.topTitle}>{titleText}</div>
            <div style={S.topSub}>{subtitleText}</div>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardHeader}>
            <div>
              <div style={S.cardTitle}>Pay to Activate (30 days)</div>
              <div style={S.cardSub}>
                You’ll be redirected to Paystack. After payment, we’ll confirm and set your renew date.
              </div>
            </div>

            <div style={S.amountPill}>
              <div style={S.amountTop}>Amount</div>
              <div style={S.amountMain}>{amountLabel}</div>
            </div>
          </div>

          <div style={S.divider} />

          <div style={S.infoGrid}>
            <div style={S.infoItem}>
              <div style={S.infoIcon}>📅</div>
              <div>
                <div style={S.infoTitle}>Renew date saved</div>
                <div style={S.infoText}>
                  After payment, your store doc will get <b>activeUntil</b> (renew date).
                </div>
              </div>
            </div>

            <div style={S.infoItem}>
              <div style={S.infoIcon}>🔒</div>
              <div>
                <div style={S.infoTitle}>Secure checkout</div>
                <div style={S.infoText}>
                  Paystack handles the payment securely.
                </div>
              </div>
            </div>
          </div>

          {safe(msg) ? (
            <div
              style={{
                ...S.msgBox,
                ...(msgType === "error"
                  ? S.msgError
                  : msgType === "success"
                  ? S.msgSuccess
                  : S.msgInfo),
              }}
            >
              <div style={S.msgDot} />
              <div style={S.msgText}>{msg}</div>
            </div>
          ) : null}

          <button
            onClick={startPayment}
            disabled={!user || paying}
            style={{
              ...S.payBtn,
              ...(paying || !user ? S.payBtnDisabled : null),
            }}
          >
            <span style={S.payBtnIcon}>▦</span>
            <span>{paying ? "Redirecting to Paystack…" : "Pay with Paystack"}</span>
          </button>

          <div style={S.trustRow}>
            <div style={S.trustBadge}>🔒 Secured</div>
            <div style={S.trustText}>Payments processed safely by Paystack</div>
          </div>

          <div style={S.footerNote}>
            After payment you’ll return to the callback page where your renew date is saved.
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    width: "100%",
    background:
      "radial-gradient(1200px 600px at 20% 0%, rgba(37,99,235,0.12), transparent 60%), radial-gradient(900px 500px at 90% 20%, rgba(14,165,233,0.12), transparent 55%), #f6f8ff",
    padding: 16,
    color: "#0f172a",
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  },
  wrap: { maxWidth: 860, margin: "0 auto", width: "100%" },

  topbar: { display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 14 },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #e7ecf6",
    background: "rgba(255,255,255,0.9)",
    borderRadius: 14,
    padding: "10px 12px",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
    whiteSpace: "nowrap",
  },
  topTitleWrap: { flex: 1, minWidth: 0 },
  topTitle: { fontSize: 22, fontWeight: 1100, letterSpacing: "-0.02em" },
  topSub: { marginTop: 4, color: "#64748b", fontWeight: 900, lineHeight: 1.25 },

  card: {
    background: "rgba(255,255,255,0.92)",
    border: "1px solid #e7ecf6",
    borderRadius: 22,
    padding: 16,
    boxShadow: "0 18px 40px rgba(15,23,42,0.08)",
    backdropFilter: "blur(8px)",
  },

  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  cardTitle: { fontSize: 20, fontWeight: 1200, letterSpacing: "-0.02em" },
  cardSub: { marginTop: 6, color: "#64748b", fontWeight: 900, maxWidth: 520 },

  amountPill: {
    border: "1px solid rgba(37,99,235,0.18)",
    background:
      "linear-gradient(135deg, rgba(37,99,235,0.10), rgba(14,165,233,0.10))",
    borderRadius: 18,
    padding: "10px 12px",
    minWidth: 170,
  },
  amountTop: { fontSize: 12, fontWeight: 1000, color: "#334155" },
  amountMain: { marginTop: 2, fontSize: 18, fontWeight: 1200, letterSpacing: "-0.02em" },

  divider: { height: 1, background: "#eef2ff", marginTop: 14, marginBottom: 14 },

  infoGrid: { display: "grid", gridTemplateColumns: "1fr", gap: 10, marginBottom: 12 },
  infoItem: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    padding: 12,
    borderRadius: 16,
    border: "1px solid #eef2ff",
    background: "#ffffff",
  },
  infoIcon: {
    width: 34,
    height: 34,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: "rgba(15,23,42,0.04)",
    fontSize: 16,
  },
  infoTitle: { fontWeight: 1100, letterSpacing: "-0.01em" },
  infoText: { marginTop: 4, color: "#64748b", fontWeight: 900, lineHeight: 1.25 },

  msgBox: {
    marginTop: 10,
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    borderRadius: 16,
    padding: 12,
    border: "1px solid #e7ecf6",
    background: "#ffffff",
  },
  msgInfo: { borderColor: "rgba(37,99,235,0.22)", background: "rgba(37,99,235,0.06)" },
  msgError: { borderColor: "rgba(255,70,120,0.35)", background: "rgba(255,70,120,0.08)" },
  msgSuccess: { borderColor: "rgba(34,197,94,0.25)", background: "rgba(34,197,94,0.08)" },
  msgDot: { marginTop: 4, width: 10, height: 10, borderRadius: 99, background: "#94a3b8", flex: "0 0 auto" },
  msgText: { fontWeight: 950, color: "#0f172a", lineHeight: 1.25 },

  payBtn: {
    width: "100%",
    marginTop: 12,
    border: 0,
    borderRadius: 18,
    padding: "14px 14px",
    fontWeight: 1200,
    cursor: "pointer",
    color: "#fff",
    background: "linear-gradient(135deg,#2563eb,#0ea5e9)",
    boxShadow: "0 16px 30px rgba(37,99,235,0.22)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  payBtnDisabled: { opacity: 0.65, cursor: "not-allowed", boxShadow: "none" },
  payBtnIcon: { fontWeight: 1300, fontSize: 16, opacity: 0.95 },

  trustRow: { marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "#334155" },
  trustBadge: {
    fontSize: 12,
    fontWeight: 1100,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #e7ecf6",
    background: "#ffffff",
  },
  trustText: { fontSize: 12, fontWeight: 900, color: "#64748b" },

  footerNote: { marginTop: 10, fontSize: 12, fontWeight: 900, color: "#94a3b8", textAlign: "center", lineHeight: 1.3 },

  skeletonCard: {
    background: "rgba(255,255,255,0.92)",
    border: "1px solid #e7ecf6",
    borderRadius: 22,
    padding: 16,
    boxShadow: "0 18px 40px rgba(15,23,42,0.08)",
  },
  skelLineLg: { height: 18, width: "58%", borderRadius: 10, background: "linear-gradient(90deg,#eef2ff,#ffffff,#eef2ff)", marginBottom: 10 },
  skelLine: { height: 12, width: "80%", borderRadius: 10, background: "linear-gradient(90deg,#eef2ff,#ffffff,#eef2ff)", marginBottom: 14 },
  skelBtn: { height: 46, width: "100%", borderRadius: 18, background: "linear-gradient(90deg,#dbeafe,#ffffff,#dbeafe)" },
};
